<template>
  <div>
    <div v-if="(!data || data.length === 0) && loading">
      <SingleChartLoader></SingleChartLoader>
    </div>
    <div v-if="(!data || data.length === 0) && !loading" class="text-center py-5">
      <h4>Chart data is not available.</h4>
    </div>
    <div
      v-else-if="data && data.length > 0 && yDataAvailable === true"
      class="chart-column liberated-gas-chart-column"
      :style="chartColumnStyle"
    >
      <div class="modal-tab-div">
        <div class="tab-div">
          <p>Y Axis Scale:</p>
          <v-btn-toggle
            v-model="isYLog"
            class="axis-toggle setHeight"
            mandatory
            @update:modelValue="handleYScaleToggleChange"
          >
            <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
            <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
          </v-btn-toggle>
        </div>
      </div>
      <ChartTitleTooltip :title="chartTitle" />
      <div v-if="isMounted" class="main-chart mr-md-2">
        <div :ref="chartId"></div>
      </div>
      <v-dialog
        v-model="modal"
        class="multi-chart-preview-div dashboard-chart-preview composition liberated-gas-chart-preview"
      >
        <v-card class="position-relative studio-card">
          <v-card-text>
            <div class="popup-top">
              <div class="modal-tab-div">
                <div class="tab-div">
                  <p>Y Axis Scale:</p>
                  <v-btn-toggle
                    v-model="isModalYLog"
                    class="axis-toggle setHeight"
                    mandatory
                    @update:modelValue="handleModalYScaleToggleChange"
                  >
                    <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                    <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                  </v-btn-toggle>
                </div>
              </div>
              <ChartTitleTooltip :title="chartTitle" extra-class="w-100" />
            </div>
            <div :ref="modalChartId" class="modal-chart-container"></div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
    <div v-else-if="data && data.length > 0 && yDataAvailable === false && !loading" class="text-center pt-5 pb-12">
      <h4>Chart data is not available.</h4>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import SingleChartLoader from "../Loaders/SingleChartLoader.vue";
import ChartTitleTooltip from "../DifferentialLiberation/Charts/ChartTitleTooltip.vue";
import Expand from "../../../assets/Svg/Chart/expand-btn.svg";
import downloadIcon from "../../../assets/Svg/Chart/csv_chart.svg";
import camIcon from "../../../assets/Svg/camIcon.svg";
import { mapGetters } from "vuex";
import { chartGridColor } from "@/utils/chartTheme";
import { downloadPlotlyHostAsPng } from "@/utils/plotlyPngDownload";
import {
  buildSimpleScatterLogYAxisConfig,
  collectPositiveYValuesFromSeries,
  estimateRotatedCategoryBottomMargin,
  getCompositionChartColumnHeight,
} from "@/utils/plotlyLogAxis";

import {
  buildVaporComponentYAxisTitle,
  getVaporComponentCsvValueHeader,
  VAPOR_COMPONENT_CHART_LEFT_MARGIN,
} from "@/utils/chartAxisUtils";

export default {
  name: "LiberatedGasComponentChart",
  components: {
    SingleChartLoader,
    ChartTitleTooltip,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
    toggleFraction: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      cross: require("../../../assets/Svg/Chart/chart-cross.svg"),
      chartId: "lineChart",
      chartData: [],
      modal: false,
      modalChartId: "modalLineChart",
      chartTitle: "Vapour Compositions at Various Pressure Steps",
      isMounted: false,
      yDataAvailable: true,
      yAxisScale: "linear",
      isYLog: false,
      modalYAxisScale: "linear",
      isModalYLog: false,
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    componentLabels() {
      return this.chartData?.[0]?.x || [];
    },
    chartColumnStyle() {
      return {
        height: `${getCompositionChartColumnHeight(this.componentLabels)}px`,
      };
    },
    vaporComponentValueLabel() {
      return getVaporComponentCsvValueHeader(this.toggleFraction);
    },
  },
  watch: {
    data(newVal, oldVal) {
      if (newVal !== oldVal && newVal.length > 0) {
        this.plotChart();
      }
    },
    yAxisScale() {
      this.plotChart();
    },
    isDarkMode() {
      this.plotChart();
    },
  },
  methods: {
    handleYScaleToggleChange() {
      if (this.yAxisScale === undefined) {
        this.yAxisScale = "linear";
      }

      this.yAxisScale = this.isYLog ? "log" : "linear";
      this.modalYAxisScale = this.yAxisScale;
      if (this.modalYAxisScale === "log") {
        this.isModalYLog = true;
      } else {
        this.isModalYLog = false;
      }
    },
    handleModalYScaleToggleChange() {
      if (this.modalYAxisScale === undefined) {
        this.modalYAxisScale = "linear";
      }

      this.modalYAxisScale = this.isModalYLog ? "log" : "linear";

      this.plotModalChart();
    },
    plotChart() {
      const differentiaLiberationData = this.data;
      const vaporMoleFractionsByComponent = {};
      differentiaLiberationData.forEach((step) => {
        const fluidComponent = step.fluidComponentReference;
        const vaporMoleFraction = step.vaporMoleFraction;
        const stepPressure = step.stepPressure;

        if (!vaporMoleFractionsByComponent[stepPressure]) {
          vaporMoleFractionsByComponent[stepPressure] = [];
        }

        vaporMoleFractionsByComponent[stepPressure].push({
          component: fluidComponent,
          moleFraction: vaporMoleFraction,
        });
      });

      this.chartData = Object.entries(vaporMoleFractionsByComponent).map(([component, values]) => {
        const xData = values.map((entry) => entry.component);
        const yData = values.map((entry) => entry.moleFraction);

        if (yData.every((value) => value === undefined) || yData.every((value) => value === null) || !yData) {
          this.yDataAvailable = false;
        } else {
          this.yDataAvailable = true;
        }

        return {
          name: component,
          x: xData,
          y: yData,
        };
      });

      this.$nextTick(() => {
        if (this.isMounted && this.$refs[this.chartId]) {
          this.plotChartWithData();
        }
      });
    },

    buildTraces(axisType) {
      return this.chartData.map((series) => ({
        x: series.x,
        y: series.y.map((value) => {
          const numericValue = Number(value);
          if (!Number.isFinite(numericValue)) {
            return null;
          }
          if (axisType === "log" && numericValue <= 0) {
            return null;
          }
          return numericValue;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: series.name,
        connectgaps: false,
        hovertemplate: `%{y:.3f}<extra></extra>`,
      }));
    },

    buildChartLayout(scale, isModal = false) {
      const axisType = scale || "linear";
      const traces = this.buildTraces(axisType);
      const bottomMargin = estimateRotatedCategoryBottomMargin(this.componentLabels);

      return {
        xaxis: {
          title: {
            text: "Component",
            standoff: 8,
          },
          tickangle: -45,
          tickfont: {
            size: 10,
          },
          automargin: false,
          gridcolor: chartGridColor(this.isDarkMode),
        },
        yaxis: buildSimpleScatterLogYAxisConfig(axisType, {
          title: buildVaporComponentYAxisTitle(this.toggleFraction),
          gridcolor: chartGridColor(this.isDarkMode),
          yValues: collectPositiveYValuesFromSeries(traces),
        }),
        margin: {
          l: VAPOR_COMPONENT_CHART_LEFT_MARGIN,
          r: isModal ? 120 : 80,
          b: bottomMargin,
        },
        legend: { x: 1.02, y: 1, orientation: "v" },
      };
    },

    buildPlotConfig() {
      return {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: ["lasso2d", "select2d", "pan2d", "resetscale", "zoom", "toImage"],
        modeBarButtonsToAdd: [
          {
            name: "Expand",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${Expand}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.openModal,
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.handleDownloadCSV,
          },
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.handleDownloadPNG,
          },
        ],
      };
    },

    plotChartWithData() {
      this.$nextTick(() => {
        Plotly.newPlot(
          this.$refs[this.chartId],
          this.buildTraces(this.yAxisScale || "linear"),
          this.buildChartLayout(this.yAxisScale || "linear"),
          this.buildPlotConfig()
        );
      });
    },

    plotModalChart() {
      this.$nextTick(() => {
        Plotly.Plots.resize(this.$refs[this.modalChartId]);
        Plotly.newPlot(
          this.$refs[this.modalChartId],
          this.buildTraces(this.modalYAxisScale || "linear"),
          this.buildChartLayout(this.modalYAxisScale || "linear", true),
          this.buildPlotConfig()
        );
      });
    },

    openModal() {
      this.modal = true;
      this.$nextTick(() => {
        this.plotModalChart();
      });
    },

    closeModal() {
      this.modal = false;
    },

    handleDownloadCSV() {
      if (!this.chartData.length) return;

      const fileName = `${this.chartTitle} data.csv`;
      const csvHeader = [`Component,${this.vaporComponentValueLabel}\n`];
      const csvRows = this.chartData.map((series) =>
        series.x.map((x, index) => `${x},${series.y[index] ?? ""}`).join("\n")
      );

      const csvContent = csvHeader + csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleDownloadPNG() {
      downloadPlotlyHostAsPng(this.$refs[this.chartId], this.chartTitle, {
        isDarkMode: this.isDarkMode,
      });
    },
  },
  mounted() {
    this.isMounted = true;
    if (this.data && this.data.length > 0) {
      this.plotChart();
    }
  },
};
</script>
